/**
 * Primitiva HTTP de la consola (docs/FASE-1.md §6): resuelve la URL base, hace `fetch`, valida la
 * respuesta con un esquema zod (de `@continnua/contracts` o local) y traduce cualquier fallo a
 * `ConsoleApiError`. No conoce nada de sesión ni de tokens — eso vive en `./session.ts`, que
 * importa `rawRequest`/`ConsoleApiError` de aquí para no duplicar esta lógica.
 *
 * Nota de estructura: docs/FASE-1.md §6 solo nombra `src/api/client.ts` y `src/api/session.ts`;
 * este archivo es un detalle de implementación interno que ambos comparten (evita una dependencia
 * circular entre los dos: `client.ts` necesita autenticación de `session.ts`, y `session.ts`
 * necesita esta misma primitiva para llamar a `/operators/login|refresh|logout`, que aún no
 * requieren token).
 */

import { ErrorResponseSchema, type ErrorCode } from "@continnua/contracts";
import type { z } from "zod";

/** Códigos de error propios de la consola, además de los que ya define `ErrorCode` del núcleo. */
export type ConsoleErrorCode = ErrorCode | "network_error" | "invalid_response";

/** Único tipo de error que lanza esta capa: nunca se propaga un error crudo del `fetch`. */
export class ConsoleApiError extends Error {
  readonly code: ConsoleErrorCode;
  readonly status: number;
  readonly details: unknown;

  constructor(options: { code: ConsoleErrorCode; message: string; status: number; details?: unknown }) {
    super(options.message);
    this.name = "ConsoleApiError";
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
  }
}

/** Métodos usados por el API de plataforma (docs/FASE-0.md §5). */
export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface ApiRequestInit {
  method?: HttpMethod;
  /** Se serializa con `JSON.stringify` si no es `undefined`. */
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * URL base del API: vacía en desarrollo (Vite hace proxy de `/api`, `/health` y `/ready` al IdP
 * local, ver `vite.config.ts`), o `VITE_API_BASE_URL` en producción (consola estática servida por
 * nginx, docs/FASE-1.md §6).
 */
export function apiBaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const configured = env?.VITE_API_BASE_URL;
  return configured && configured.length > 0 ? configured.replace(/\/+$/, "") : "";
}

/** Lee el cuerpo de una respuesta como JSON; `undefined` si está vacío o no es JSON válido. */
async function readJsonBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

async function toApiError(res: Response): Promise<ConsoleApiError> {
  const body = await readJsonBody(res);
  const parsed = ErrorResponseSchema.safeParse(body);
  if (parsed.success) {
    return new ConsoleApiError({
      code: parsed.data.error.code,
      message: parsed.data.error.message,
      status: res.status,
      details: parsed.data.error.details,
    });
  }
  return new ConsoleApiError({
    code: "internal",
    message: "Ocurrió un error inesperado en el servidor. Intenta de nuevo.",
    status: res.status,
  });
}

/**
 * Llama al API y valida la respuesta con `schema`. Nunca lanza un error crudo: siempre
 * `ConsoleApiError`. `schema` debe aceptar `undefined` para respuestas `204 No Content`
 * (usualmente `z.void()`).
 */
export async function rawRequest<T>(path: string, init: ApiRequestInit, schema: z.ZodType<T>): Promise<T> {
  let res: Response;
  try {
    // `body` se omite del objeto en vez de pasarse como `undefined`
    // (`exactOptionalPropertyTypes` del tsconfig de la consola).
    res = await fetch(`${apiBaseUrl()}${path}`, {
      method: init.method ?? "GET",
      headers: { "content-type": "application/json", ...(init.headers ?? {}) },
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    throw new ConsoleApiError({
      code: "network_error",
      message: "No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.",
      status: 0,
    });
  }

  if (!res.ok) {
    throw await toApiError(res);
  }

  const body = await readJsonBody(res);
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ConsoleApiError({
      code: "invalid_response",
      message: "La respuesta del servidor no coincide con el formato esperado.",
      status: res.status,
      details: parsed.error.issues,
    });
  }
  return parsed.data;
}
